import { Gov2ApiResponse } from '../services/gov2-summary.service';

/**
 * A translator callback. Typically `(key) => translateService.instant(key)`.
 * Should return the same `key` back when no translation exists.
 */
export type TranslateFn = (key: string) => string;

export interface ExtractErrorMessageOptions {
  /**
   * Translation key for the generic fallback shown when no responseCode,
   * responseMessage or message can be resolved.
   */
  fallbackKey?: string;
  /**
   * Translation key for the label preceding the traceId in the fallback.
   */
  traceLabelKey?: string;
  /**
   * Final hardcoded fallback when even the translated fallback is missing.
   */
  hardcodedFallback?: string;
  /**
   * Final hardcoded trace label when the translated one is missing.
   */
  hardcodedTraceLabel?: string;
}

const DEFAULT_OPTIONS: Required<ExtractErrorMessageOptions> = {
  fallbackKey: 'APPLICATION_ERROR_GENERIC',
  traceLabelKey: 'TRACE_ID_FOR_ASSISTANCE',
  hardcodedFallback: 'The process failed. Please try again later.',
  hardcodedTraceLabel: 'Please provide this trace ID for assistance',
};

/**
 * Patterns that indicate a low-level network/technical error message that
 * should not be shown to end users. When a responseMessage matches any of
 * these patterns it is treated as missing so the generic fallback is used.
 */
const TECHNICAL_ERROR_PATTERNS: RegExp[] = [
  /connection refused/i,
  /connection reset/i,
  /connection timed out/i,
  /connect timed out/i,
  /read timed out/i,
  /no route to host/i,
  /unknown host/i,
  /host unreachable/i,
  /network is unreachable/i,
  /socket (is )?closed/i,
  /http failure response/i,
  /feign\.retryableexception/i,
  /java\.net\./i,
  /ioexception/i,
];

function isTechnicalErrorMessage(message: string | undefined): boolean {
  if (!message) return false;
  return TECHNICAL_ERROR_PATTERNS.some(pattern => pattern.test(message));
}

/**
 * Some Hub / Gov2 errors arrive with the real payload embedded as a stringified
 * Feign/Spring error inside `responseCode`. Unwrap and merge the nested
 * `{responseCode, responseMessage}` object if present; otherwise return the
 * payload untouched.
 */
export function resolveBackendErrorBody(payload: any): any {
  if (!payload || typeof payload !== 'object') {
    return payload;
  }

  const rawResponseCode = payload.responseCode;
  if (typeof rawResponseCode === 'string' && rawResponseCode.includes('{')) {
    const start = rawResponseCode.indexOf('{');
    const end = rawResponseCode.lastIndexOf('}');
    if (start !== -1 && end !== -1 && end > start) {
      try {
        const nested = JSON.parse(rawResponseCode.substring(start, end + 1));
        if (nested && typeof nested === 'object') {
          return { ...payload, ...nested };
        }
      } catch {
        // fall through and return the original payload
      }
    }
  }

  return payload;
}

/**
 * Extract a user-friendly error message from a Hub / Gov2 error or response
 * payload.
 *
 * Resolution order:
 *  1. Translation of responseCode
 *  2. Translation of responseMessage
 *  3. responseMessage as-is
 *  4. Generic fallback (translated) + traceId when available
 *
 * Accepts either an `HttpErrorResponse` (where the backend body lives in
 * `.error`) or a plain response body.
 */
export function extractErrorMessage(
  payload: any,
  translate: TranslateFn,
  options: ExtractErrorMessageOptions = {}
): string {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const errorBody = resolveBackendErrorBody(payload?.error ?? payload);

  const responseCode: string | undefined = errorBody?.responseCode;
  const rawResponseMessage: string | undefined =
    errorBody?.responseMessage || errorBody?.message;
  const traceId: string | undefined = errorBody?.traceId;

  // Hide low-level network / stack-trace messages from end users; fall back
  // to the translated generic message instead.
  const responseMessage = isTechnicalErrorMessage(rawResponseMessage)
    ? undefined
    : rawResponseMessage;

  const tryTranslate = (key: string | undefined): string | null => {
    if (!key) return null;
    const translated = translate(key);
    return translated && translated !== key ? translated : null;
  };

  const codeTranslation = tryTranslate(responseCode);
  if (codeTranslation) return codeTranslation;

  const messageTranslation = tryTranslate(responseMessage);
  if (messageTranslation) return messageTranslation;

  if (responseMessage) return responseMessage;

  const fallback = tryTranslate(opts.fallbackKey) || opts.hardcodedFallback;

  if (traceId) {
    const traceLabel =
      tryTranslate(opts.traceLabelKey) || opts.hardcodedTraceLabel;
    return `${fallback} ${traceLabel}: ${traceId}`;
  }

  return fallback;
}

/**
 * Normalize an HttpErrorResponse into a Gov2ApiResponse so downstream
 * consumers can surface backend fields (responseCode, responseMessage,
 * traceId) without needing to inspect the raw HttpErrorResponse.
 *
 * Falls back to the HttpErrorResponse's own message only when the backend
 * did not return a structured body.
 */
export function normalizeHttpErrorToGov2Response<T>(
  error: any,
  defaultMessage: string
): Gov2ApiResponse<T> {
  const body = error?.error;

  if (body && typeof body === 'object') {
    return {
      ...body,
      status: false,
      message:
        body.responseMessage ||
        body.message ||
        body.responseCode ||
        defaultMessage,
    };
  }

  return {
    status: false,
    message:
      (typeof body === 'string' && body) || error?.message || defaultMessage,
  };
}
